'use client';

import React, { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import { Section } from '@/components/ui/Section';
import styles from './AYCEShowcase.module.css';

export interface AYCEShowcaseProps {
  title: string;
  subtitle: string;
  altTexts?: {
    shabuShabuQr?: string;
    shabuShabuTablet?: string;
    okiniiQr?: string;
    okiniiTablet?: string;
    brasserieQr?: string;
    brasserieTablet?: string;
    doeleQr?: string;
    doeleTablet?: string;
  };
}

// AYCE Mockup configuration with brand-specific glow colors
// Professional layout: QR (mobile) + Tablet combination
const AYCE_MOCKUP_CONFIG = [
  {
    id: 'shabu-shabu',
    qr: '/images/ayce-case-studies/ayce-qr-ordering-shabu-shabu-menu-1.webp',
    tablet: '/images/ayce-case-studies/ayce-tablet-ordering-shabu-shabu-menu-1.webp',
    color: '#c00f0f', // Shabu Shabu red
  },
  {
    id: 'okinii',
    qr: '/images/ayce-case-studies/ayce-qr-ordering-okinii-menu-1.webp',
    tablet: '/images/ayce-case-studies/ayce-tablet-ordering-okinii-menu-1.webp',
    color: '#b30105', // Okinii dark red
  },
  {
    id: 'brasserie',
    qr: '/images/ayce-case-studies/ayce-qr-ordering-brasserie-bij-ons-menu-1.webp',
    tablet: '/images/ayce-case-studies/ayce-tablet-ordering-brasserie-bij-ons-menu-1.webp',
    color: '#9c100d', // Brasserie bij Ons red
  },
  {
    id: 'doele',
    qr: '/images/ayce-case-studies/ayce-qr-ordering-restaurant-de-doele-menu-1.webp',
    tablet: '/images/ayce-case-studies/ayce-tablet-ordering-restaurant-de-doele-menu-1.webp',
    color: '#f46e27', // Restaurant De Doele orange
  },
] as const;

/**
 * AYCEShowcase Component
 * Displays 4 All You Can Eat mockups with floating animation effect
 * Each mockup shows QR ordering + Tablet ordering combined
 * On mobile: auto-playing slider with touch support
 */
export function AYCEShowcase({ title, subtitle, altTexts }: AYCEShowcaseProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-play slider on mobile
  useEffect(() => {
    if (!isMobile) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 4);
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, [isMobile]);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible);
          }
        });
      },
      { threshold: 0.1 }
    );

    const ayceMockups = sectionRef.current?.querySelectorAll(`.${styles.ayceCombo}`);
    ayceMockups?.forEach((mockup) => observer.observe(mockup));

    return () => observer.disconnect();
  }, []);

  // Touch handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current - touchEndX.current > 75) {
      // Swipe left
      setCurrentSlide((prev) => (prev + 1) % 4);
    }
    if (touchStartX.current - touchEndX.current < -75) {
      // Swipe right
      setCurrentSlide((prev) => (prev - 1 + 4) % 4);
    }
  };

  // Build alt text keys for each mockup
  const getAltTexts = (id: string) => {
    const keyPrefix = id.replace(/-/g, '');
    return {
      qr: altTexts?.[`${keyPrefix}Qr` as keyof typeof altTexts] || `Jamezz All You Can Eat QR ordering - ${id}`,
      tablet: altTexts?.[`${keyPrefix}Tablet` as keyof typeof altTexts] || `Jamezz All You Can Eat tablet ordering - ${id}`,
    };
  };

  return (
    <Section background="primary" paddingY="none">
      <div className={styles.showcase} ref={sectionRef}>
        <div className={styles.background}>
          <div className={styles.gradientOrb1} />
          <div className={styles.gradientOrb2} />
          <div className={styles.grid} />
        </div>

        <div className={styles.content}>
          <div className={styles.header}>
            <h1 className={styles.title}>{title}</h1>
            <p className={styles.subtitle}>{subtitle}</p>
          </div>

          <div 
            className={styles.ayceCombosGrid}
            ref={sliderRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={isMobile ? {
              transform: `translateX(-${currentSlide * 100}%)`,
              transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            } : {}}
          >
            {AYCE_MOCKUP_CONFIG.map((mockup, index) => {
              const altTexts = getAltTexts(mockup.id);
              
              return (
                <div
                  key={mockup.id}
                  className={styles.ayceCombo}
                  style={{
                    animationDelay: `${index * 0.15}s`,
                  }}
                >
                  <div className={styles.ayceComboInner}>
                    <div
                      className={styles.ayceComboGlow}
                      style={{
                        background: `radial-gradient(circle at center, ${mockup.color}40 0%, transparent 70%)`,
                      }}
                    />
                    <div className={styles.deviceWrapper}>
                      {/* Tablet - Background (slightly to the left and rotated) */}
                      <div className={styles.tabletWrapper}>
                        <Image
                          src={mockup.tablet}
                          alt={altTexts.tablet}
                          width={500}
                          height={700}
                          className={styles.tabletImage}
                          loading="eager"
                          priority={true}
                          quality={85}
                        />
                      </div>
                      
                      {/* QR (Mobile) - Foreground (positioned in front, slightly to the right and rotated) */}
                      <div className={styles.qrWrapper}>
                        <Image
                          src={mockup.qr}
                          alt={altTexts.qr}
                          width={350}
                          height={700}
                          className={styles.qrImage}
                          loading="eager"
                          priority={true}
                          quality={85}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Navigation dots - only on mobile */}
          {isMobile && (
            <div className={styles.sliderDots}>
              {AYCE_MOCKUP_CONFIG.map((mockup, index) => (
                <button
                  key={mockup.id}
                  className={`${styles.dot} ${currentSlide === index ? styles.active : ''}`}
                  onClick={() => setCurrentSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                  style={currentSlide === index ? {
                    backgroundColor: mockup.color,
                  } : {}}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}

